import { ApiClient } from "../network/ApiClient";
import { ApiEndpoints } from "../network/ApiEndpoints";

interface DownloadResponse {
  code: number | string;
  // bt-core M1: data is now { url, fileName }; an un-upgraded server still returns a bare URL string.
  data: { url?: string; fileName?: string } | string;
  message?: string;
}

interface DownloadDocumentsRequest {
  documentIds: number[];
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const downloadDocuments = async ({
  documentIds,
  onSuccess,
  onError,
  onLoadingChange,
}: DownloadDocumentsRequest): Promise<void> => {
  try {
    onLoadingChange?.(true);

    const response = await ApiClient.post<DownloadDocumentsRequest, DownloadResponse>(
      ApiEndpoints.downloadDocuments(),
      { documentIds: documentIds }
    );

    if (response?.code === 200 || response?.code === "200") {
      const data = response?.data as { url?: string; fileName?: string } | string;
      const downloadUrl = String((typeof data === "object" ? data?.url : undefined) ?? data);
      fetch(downloadUrl)
        .then((res) => res.blob())
        .then((blob) => {
          // Prefer the server-supplied friendly name (M1); fall back to deriving from the URL
          // (un-upgraded server, where data is a bare URL string → ugly GUID name).
          let filename = (typeof data === "object" && data?.fileName) || '';
          if (!filename) {
            filename = 'document';
            try {
              const urlObj = new URL(downloadUrl);
              const pathParts = urlObj.pathname.split('/');
              const lastPart = pathParts[pathParts.length - 1];

              if (lastPart) {
                filename = decodeURIComponent(lastPart);
              }
            } catch (error) {
              console.warn('Could not extract filename from URL, using default:', error);
            }
          }

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error('Download failed:', error);
          throw error;
        });

      onSuccess?.();
    } else {
      throw new Error(response?.message || "Unknown error from API");
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    console.error("Download error:", errorMessage);
    onError?.(errorMessage);
  } finally {
    onLoadingChange?.(false);
  }
};